import { useState, useRef, useEffect } from "react";
import React from "react";
import Tippy from "@tippyjs/react";
import { CloseCircleFilled, SearchOutlined } from "@ant-design/icons";
import { Input, Spin } from "antd";

const PopupCellRenderer = ({ params, royaltySalesRequests, isLoading }) => {
  const tippyRef = useRef();
  const [visible, setVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  const show = () => setVisible(true);
  const hide = () => {
    setVisible(false);
    setSearchTerm("");
  };

  // Helper function to get filtered dropdown values
  const getFilteredValues = () => {
    if (!royaltySalesRequests || royaltySalesRequests.length === 0) {
      return [];
    }

    const { country_name, product_name } = params.data;

    return royaltySalesRequests
      .filter((item) => {
        // Check if this request matches the current row's country and product
        const hasMatchingSalesData = item.sales_data.some(sale => 
          sale.country_id.country_name === country_name &&
          sale.product_id.product_name === product_name
        );
        
        // Also check if the request itself matches (for requests without sales_data)
        const requestMatches = 
          item.country_id.country_name === country_name &&
          item.product_id.product_name === product_name;

        return hasMatchingSalesData || requestMatches;
      })
      .map((item) => {
        // Use sales_data if available, otherwise use the main request data
        const salesData = item.sales_data && item.sales_data.length > 0 
          ? item.sales_data[0] 
          : null;

        return {
          request_id: item.request_id,
          mapping_id: item.mapping_id,
          quantity: item.quantity,
          expected_date_of_sale: salesData 
            ? `${salesData.month} ${salesData.year}`
            : item.expected_date_of_sale 
              ? new Date(item.expected_date_of_sale).toLocaleDateString()
              : 'Not specified',
          country_name: salesData 
            ? salesData.country_id.country_name 
            : item.country_id.country_name,
          product_name: salesData 
            ? salesData.product_id.product_name 
            : item.product_id.product_name,
          buyer_name_and_address: item.buyer_name_and_address || 'Not specified'
        };
      });
  };

  useEffect(() => {
    // Filter data based on search term (only by mapping_id)
    const availableData = getFilteredValues();
    if (availableData && availableData.length > 0) {
      const filtered = availableData.filter(item => 
        item.mapping_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData([]);
    }
  }, [searchTerm, royaltySalesRequests, params.data]);

  const dropDownContent = (
    <div className="menu-container">
      <div className="search-container">
        <Input
          placeholder="Search ID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          style={{ marginBottom: "10px" }}
        />
      </div>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '8px' }}>
          <Spin size="default" />
        </div>
      ) : filteredData.length > 0 ? (
        filteredData.map((value, index) => (
          <div
            key={index}
            onClick={() => onClickHandler(value.mapping_id)}
            className="menu-parent"
          >
            <div className="menu-item">{value.mapping_id}</div>
            <div className="menu-child">
              <div className="menu-subchild">
                <span className="menu-subchild-label">Quantity: </span>
                <span>{value.quantity}</span>
              </div>
              <div className="menu-subchild">
                <span className="menu-subchild-label">Country: </span>
                <span>{value.country_name}</span>
              </div>
              <div className="menu-subchild">
                <span className="menu-subchild-label">Product: </span>
                <span>{value.product_name}</span>
              </div>
              {value.buyer_name_and_address && (
              <div className="menu-subchild">
                  <span className="menu-subchild-label">Buyer: </span>
                  <span>{value.buyer_name_and_address}</span>
              </div>
              )}
            </div>
          </div>
        ))
      ) : (
        <div className="no-results">
          {isLoading ? "Loading..." : "No matching requests found"}
        </div>
      )}
    </div>
  );

  const onClickHandler = (request_id__mapping_id) => {
    hide();
    params.data.request_id__mapping_id = request_id__mapping_id;
    const colId = params.column.getColId();
    params.api.refreshCells({ rowNodes: [params.node], columns: [colId] });
  };

  const onClickClearHandler = () => {
    hide();
    params.data.request_id__mapping_id = null;
    const colId = params.column.getColId();
    params.api.refreshCells({ rowNodes: [params.node], columns: [colId] });
  };

  return (
    <Tippy
      ref={tippyRef}
      content={dropDownContent}
      visible={visible}
      onClickOutside={hide}
      allowHTML={true}
      arrow={false}
      appendTo={document.body}
      interactive={true}
      placement="auto"
    >
      <div>
        <button className="request_id_btn" onClick={visible ? hide : show}>
          {params.data.request_id__mapping_id}
        </button>
        {params.data.request_id__mapping_id && (
          <button
            className="request_id_cross_btn"
            onClick={() => onClickClearHandler()}
          >
            <CloseCircleFilled />
          </button>
        )}
      </div>
    </Tippy>
  );
};

export default PopupCellRenderer;